import { useEffect, useRef, useCallback } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { useShittyMapStore } from '@/hooks/useShittyMapStore';

export default function ShittyMapControls() {
  const { camera, gl } = useThree();
  const { focusTarget, clearFocusTarget, setCameraPosition } = useShittyMapStore();

  const isDragging = useRef(false);
  const lastPosition = useRef({ x: 0, y: 0 });

  const animationRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startZoom: 0,
    targetX: 0,
    targetY: 0,
    targetZoom: 0,
    startTime: 0,
    duration: 0,
  });

  useEffect(() => {
    if (focusTarget) {
      const { x, y, zoom, duration = 800 } = focusTarget;

      animationRef.current = {
        active: true,
        startX: camera.position.x,
        startY: camera.position.y,
        startZoom: camera.zoom,
        targetX: x,
        targetY: y,
        targetZoom: zoom,
        startTime: performance.now(),
        duration,
      };

      clearFocusTarget();
    }
  }, [focusTarget, camera, clearFocusTarget]);

  useFrame(() => {
    const anim = animationRef.current;
    if (!anim.active) return;

    const elapsed = performance.now() - anim.startTime;
    const progress = Math.min(elapsed / anim.duration, 1);

    const eased = 1 - Math.pow(1 - progress, 4);

    camera.position.x = anim.startX + (anim.targetX - anim.startX) * eased;
    camera.position.y = anim.startY + (anim.targetY - anim.startY) * eased;
    camera.zoom = anim.startZoom + (anim.targetZoom - anim.startZoom) * eased;
    camera.updateProjectionMatrix();

    if (progress >= 1) {
      anim.active = false;
      setCameraPosition({ x: camera.position.x, y: camera.position.y, zoom: camera.zoom });
    }
  });

  const handleMouseDown = useCallback((e) => {
    isDragging.current = true;
    lastPosition.current = { x: e.clientX, y: e.clientY };
    gl.domElement.style.cursor = 'grabbing';
  }, [gl]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current || animationRef.current.active) return;

    const deltaX = e.clientX - lastPosition.current.x;
    const deltaY = e.clientY - lastPosition.current.y;

    const panSpeed = 1.5 / camera.zoom;
    camera.position.x -= deltaX * panSpeed;
    camera.position.y += deltaY * panSpeed;

    lastPosition.current = { x: e.clientX, y: e.clientY };
    setCameraPosition({ x: camera.position.x, y: camera.position.y, zoom: camera.zoom });
  }, [camera, setCameraPosition]);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
    gl.domElement.style.cursor = 'grab';
  }, [gl]);

  const handleWheel = useCallback((e) => {
    const isModifierHeld = e.metaKey || e.ctrlKey;

    if (isModifierHeld) {
      e.preventDefault();

      const delta = e.deltaY > 0 ? -0.15 : 0.15;
      const newZoom = Math.max(0.15, Math.min(5, camera.zoom * (1 + delta)));

      camera.zoom = newZoom;
      camera.updateProjectionMatrix();
      setCameraPosition({ x: camera.position.x, y: camera.position.y, zoom: newZoom });
    }
  }, [camera, setCameraPosition]);

  useEffect(() => {
    const canvas = gl.domElement;

    canvas.style.cursor = 'grab';

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseleave', handleMouseUp);
    canvas.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseleave', handleMouseUp);
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, [gl, handleMouseDown, handleMouseMove, handleMouseUp, handleWheel]);

  return null;
}
